import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { vcollectProjectsApi, vcollectFormsApi } from '../../api/vcollect';
import { VCOLLECT_STATUS_LABELS } from './VCollectProjectsListPage';
import { vcollectProjectUsersApi, PROJECT_ROLE_LABELS } from '../../api/vcollect-project-user';
import { vcollectEnumeratorsApi, VCollectAssignment } from '../../api/vcollect-enumerator';
import { projectLocationsApi } from '../../api/projectLocations';
import { vcollectSubmissionsApi, SUBMISSION_STATUS_LABELS } from '../../api/vcollect-submission';
import { genericDocumentApi } from '../../api/generic-documents';
import { usersApi } from '../../api/admin/users';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { RecordHeader } from '../../components/RecordHeader';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { Modal } from '../../components/Modal';
import { DocumentsSection } from '../../components/DocumentsSection';
import { useToast } from '../../components/Toast';
import { VERSION_STATUS_LABELS } from './VCollectFormsListPage';

function AssignmentProgressCell({ assignment }: { assignment: VCollectAssignment }) {
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['vcollect-assignment-progress', assignment.id],
    queryFn: () => vcollectEnumeratorsApi.getAssignmentProgress(assignment.id),
    enabled: expanded,
  });
  if (!expanded) return <button className="btn-secondary" onClick={() => setExpanded(true)}>View Progress</button>;
  if (isLoading) return <span className="muted">Loading…</span>;
  const p = data?.data;
  if (!p) return <span className="muted">-</span>;
  return (
    <span style={{ fontSize: 12 }}>
      {p.targetCount ? `${p.submitted} / ${p.targetCount} ${p.targetDescription ?? ''}` : `${p.submitted} submitted`}
      {' · '}{p.verified} verified{p.remaining !== null ? ` · ${p.remaining} remaining` : ''}
    </span>
  );
}

const PROJECT_ROLES = Object.keys(PROJECT_ROLE_LABELS);

export function VCollectProjectWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const projectId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data: projectData, isError } = useQuery({ queryKey: ['vcollect-project', projectId], queryFn: () => vcollectProjectsApi.get(projectId) });
  const project = projectData?.data;

  const { data: formsData } = useQuery({ queryKey: ['vcollect-forms', projectId], queryFn: () => vcollectFormsApi.list(projectId) });
  const forms = formsData?.data ?? [];
  const { data: projectUsersData } = useQuery({ queryKey: ['vcollect-project-users', projectId], queryFn: () => vcollectProjectUsersApi.list(projectId) });
  const projectUsers = projectUsersData?.data ?? [];
  const { data: assignmentsData } = useQuery({ queryKey: ['vcollect-assignments', projectId], queryFn: () => vcollectEnumeratorsApi.listAssignments({ vcollectProjectId: projectId }) });
  const assignments = assignmentsData?.data ?? [];
  const { data: submissionsData } = useQuery({ queryKey: ['vcollect-submissions', projectId], queryFn: () => vcollectSubmissionsApi.list({ vcollectProjectId: projectId }) });
  const submissions = submissionsData?.data ?? [];
  const { data: attentionData } = useQuery({ queryKey: ['vcollect-attention', projectId], queryFn: () => vcollectProjectsApi.getAttentionItems(projectId) });
  const attention = attentionData?.data;
  const { data: docsData } = useQuery({ queryKey: ['vcollect-project-documents', projectId], queryFn: () => genericDocumentApi.listFor('vcollect_project', projectId) });
  const { data: locationSummaryData } = useQuery({ queryKey: ['vcollect-location-summary', projectId], queryFn: () => vcollectEnumeratorsApi.getLocationSummary(projectId) });
  const locationSummary = locationSummaryData?.data ?? [];

  // ---- Grant access modal ----
  const [showGrantModal, setShowGrantModal] = useState(false);
  const { data: allUsersData } = useQuery({ queryKey: ['admin-users'], queryFn: () => usersApi.list(), enabled: showGrantModal });
  const allUsers = allUsersData?.data ?? [];
  const { register, handleSubmit, reset, watch } = useForm<{ userId: number; projectRole: string; accessScope: string; formIds: string[] }>({ defaultValues: { accessScope: 'entire_project' } });
  const accessScope = watch('accessScope');

  const grantMutation = useMutation({
    mutationFn: (input: { userId: number; projectRole: string; accessScope: string; formIds?: number[] }) => vcollectProjectUsersApi.grant(projectId, { ...input, userId: Number(input.userId) }),
    onSuccess: () => { showSuccess('Access granted.'); queryClient.invalidateQueries({ queryKey: ['vcollect-project-users', projectId] }); setShowGrantModal(false); reset(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const revokeMutation = useMutation({
    mutationFn: (membershipId: number) => vcollectProjectUsersApi.revoke(membershipId),
    onSuccess: () => { showSuccess('Access revoked.'); queryClient.invalidateQueries({ queryKey: ['vcollect-project-users', projectId] }); },
    onError: (err: ApiError) => showError(err.message),
  });

  // ---- Create assignment modal ----
  const [showAssignModal, setShowAssignModal] = useState(false);
  const { data: enumeratorsData } = useQuery({ queryKey: ['vcollect-enumerators'], queryFn: () => vcollectEnumeratorsApi.list(), enabled: showAssignModal });
  const enumerators = enumeratorsData?.data ?? [];
  const { data: teamsData } = useQuery({ queryKey: ['vcollect-teams', projectId], queryFn: () => vcollectEnumeratorsApi.listTeams(), enabled: showAssignModal });
  const teams = teamsData?.data ?? [];
  const assignForm = useForm<{ assigneeType: string; assigneeId: number; formId?: number; stateId?: string; locationId?: number; collectionStartDate?: string; collectionEndDate?: string; targetCount?: number; targetDescription?: string }>({ defaultValues: { assigneeType: 'enumerator' } });
  const assigneeType = assignForm.watch('assigneeType');
  const selectedStateId = assignForm.watch('stateId');
  const { data: statesData } = useQuery({ queryKey: ['location-states'], queryFn: () => projectLocationsApi.states(), enabled: showAssignModal });
  const states = statesData?.data ?? [];
  const { data: lgasData } = useQuery({ queryKey: ['location-lgas', selectedStateId], queryFn: () => projectLocationsApi.lgasForState(Number(selectedStateId)), enabled: !!selectedStateId });
  const lgas = lgasData?.data ?? [];

  const createAssignmentMutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => {
      const { stateId: _stateId, ...rest } = input;
      return vcollectEnumeratorsApi.createAssignment({ ...rest, vcollectProjectId: projectId, assigneeId: Number(input.assigneeId), formId: input.formId ? Number(input.formId) : undefined, locationId: input.locationId ? Number(input.locationId) : undefined, targetCount: input.targetCount ? Number(input.targetCount) : undefined });
    },
    onSuccess: () => { showSuccess('Assignment created.'); queryClient.invalidateQueries({ queryKey: ['vcollect-assignments', projectId] }); setShowAssignModal(false); assignForm.reset(); },
    onError: (err: ApiError) => showError(err.message),
  });

  if (isError) return <div className="page"><div className="error-state">Failed to load this project. Please try refreshing the page.</div></div>;
  if (!project) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;

  const activeForms = forms.filter((f) => f.latest_version_status === 'active').length;
  const draftForms = forms.filter((f) => f.latest_version_status === 'draft').length;
  const verifiedCount = submissions.filter((s) => s.review_status === 'verified').length;
  const flaggedCount = submissions.filter((s) => s.review_status === 'flagged').length;
  const activeMemberships = projectUsers.filter((u) => u.status === 'active');
  const uniqueEnumerators = new Set(assignments.filter((a) => a.assignee_type === 'enumerator').map((a) => a.assignee_id)).size;

  return (
    <div className="page">
      <a onClick={() => navigate('/vcollect')} className="table-link">← Data Collection Projects</a>
      <RecordHeader
        title={project.name}
        readOnly={true}
        entityType="vcollect_project"
        entityId={projectId}
        fields={[
          { label: 'Reference', value: project.reference },
          { label: 'Programme', value: project.programme_name ?? '-' },
          { label: 'Linked Project', value: project.project_name ?? '-' },
          { label: 'Status', value: <StatusBadge status={project.status} label={VCOLLECT_STATUS_LABELS[project.status]} /> },
        ]}
      />

      <WorkspaceTabs
        tabs={[
          {
            key: 'overview', label: 'Overview', content: (
              <div>
                {attention && (attention.formsAwaitingPublication > 0 || attention.flaggedSubmissions > 0 || attention.unresolvedQualityFlags > 0) && (
                  <>
                    <SectionHeader title="Needs Attention" />
                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {attention.formsAwaitingPublication > 0 && (
                        <li className="warning-state"><a onClick={() => navigate(`/vcollect/forms?vcollectProjectId=${projectId}`)} style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>{attention.formsAwaitingPublication} form(s) approved and awaiting publication</a></li>
                      )}
                      {attention.flaggedSubmissions > 0 && (
                        <li className="warning-state"><a onClick={() => navigate(`/vcollect/submissions?vcollectProjectId=${projectId}&reviewStatus=flagged`)} style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>{attention.flaggedSubmissions} flagged submission(s) need review</a></li>
                      )}
                      {attention.unresolvedQualityFlags > 0 && (
                        <li className="warning-state"><a onClick={() => navigate(`/vcollect/submissions?vcollectProjectId=${projectId}`)} style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>{attention.unresolvedQualityFlags} unresolved data quality flag(s)</a></li>
                      )}
                    </ul>
                  </>
                )}

                <SectionHeader title="Forms" />
                <p>{forms.length} total &middot; {activeForms} active &middot; {draftForms} draft</p>

                <SectionHeader title="Data Collection" />
                <p>{submissions.length} submissions &middot; {verifiedCount} verified &middot; {flaggedCount} flagged</p>

                <SectionHeader title="Field Team" />
                <p>{uniqueEnumerators} enumerator(s) assigned &middot; {activeMemberships.length} project user(s)</p>
              </div>
            ),
          },
          {
            key: 'forms', label: `Forms (${forms.length})`, content: (
              <div>
                <SectionHeader title="Forms" actions={<button onClick={() => navigate(`/vcollect/forms?vcollectProjectId=${projectId}`)}>+ New Form</button>} />
                <DataTable
                  rows={forms}
                  rowKey={(f) => f.id}
                  emptyMessage="No forms in this project yet."
                  columns={[
                    { header: 'Title', render: (f) => <a onClick={() => navigate(`/vcollect/forms/${f.id}`)} className="table-link">{f.title}</a> },
                    { header: 'Form ID', render: (f) => f.form_id },
                    { header: 'Version', render: (f) => f.latest_version_number ? `v${f.latest_version_number}` : '-' },
                    { header: 'Status', render: (f) => f.latest_version_status ? <StatusBadge status={f.latest_version_status} label={VERSION_STATUS_LABELS[f.latest_version_status]} /> : '-' },
                  ]}
                />
              </div>
            ),
          },
          {
            key: 'team', label: `Team (${activeMemberships.length})`, content: (
              <div>
                <SectionHeader title="Project Users" actions={<button onClick={() => setShowGrantModal(true)}>+ Add User</button>} />
                <DataTable
                  rows={projectUsers}
                  rowKey={(u) => u.id}
                  emptyMessage="No users have been granted access to this project yet."
                  columns={[
                    { header: 'User', render: (u) => u.full_name },
                    { header: 'Project Role', render: (u) => PROJECT_ROLE_LABELS[u.project_role] ?? u.project_role },
                    { header: 'Scope', render: (u) => u.access_scope === 'entire_project' ? 'Entire Project' : 'Specific Forms' },
                    { header: 'Status', render: (u) => <StatusBadge status={u.status} label={u.status === 'active' ? 'Active' : 'Revoked'} /> },
                    { header: '', render: (u) => u.status === 'active' && <button className="btn-secondary" onClick={() => { if (confirm(`Revoke ${u.full_name}'s access?`)) revokeMutation.mutate(u.id); }}>Revoke</button> },
                  ]}
                />
              </div>
            ),
          },
          {
            key: 'enumerators', label: `Enumerators (${uniqueEnumerators})`, content: (
              <div>
                <SectionHeader title="Field Assignments" actions={
                  <>
                    <button className="btn-secondary" onClick={() => navigate(`/vcollect/teams?vcollectProjectId=${projectId}`)}>Manage Teams</button>
                    <button className="btn-secondary" onClick={() => navigate('/vcollect/enumerators')}>Manage Enumerators</button>
                    <button onClick={() => setShowAssignModal(true)}>+ Create Assignment</button>
                  </>
                } />
                <DataTable
                  rows={assignments}
                  rowKey={(a) => a.id}
                  emptyMessage="No enumerators or teams assigned to this project yet."
                  columns={[
                    { header: 'Assignee', render: (a) => a.assignee_name ?? `#${a.assignee_id}` },
                    { header: 'Type', render: (a) => a.assignee_type === 'enumerator' ? 'Enumerator' : 'Team' },
                    { header: 'Form', render: (a) => a.form_title ?? 'Whole Project' },
                    { header: 'Period', render: (a) => a.collection_start_date ? `${a.collection_start_date} – ${a.collection_end_date ?? 'ongoing'}` : '-' },
                    { header: 'Status', render: (a) => <StatusBadge status={a.status} /> },
                    { header: 'Progress', render: (a) => <AssignmentProgressCell assignment={a} /> },
                  ]}
                />
              </div>
            ),
          },
          {
            key: 'locations', label: `Locations (${locationSummary.length})`, content: (
              <div>
                <SectionHeader title="Assignments by Location" />
                <DataTable
                  rows={locationSummary}
                  rowKey={(l) => l.location_id}
                  emptyMessage="No assignments have a location set yet."
                  columns={[
                    { header: 'Location', render: (l) => l.location_name },
                    { header: 'Level', render: (l) => l.level.toUpperCase() },
                    { header: 'Enumerator Assignments', render: (l) => l.enumerator_assignment_count },
                    { header: 'Team Assignments', render: (l) => l.team_assignment_count },
                    { header: 'Total', render: (l) => l.assignment_count },
                  ]}
                />
              </div>
            ),
          },
          {
            key: 'submissions', label: `Submissions (${submissions.length})`, content: (
              <div>
                <SectionHeader title="Submissions — All Forms" />
                <DataTable
                  rows={submissions}
                  rowKey={(s) => s.id}
                  emptyMessage="No submissions synced for this project yet."
                  columns={[
                    { header: 'Reference', render: (s) => <a onClick={() => navigate(`/vcollect/submissions/${s.id}`)} className="table-link">{s.submission_reference}</a> },
                    { header: 'Form', render: (s) => s.form_title ?? '-' },
                    { header: 'Enumerator', render: (s) => s.enumerator_name ?? 'Unknown' },
                    { header: 'Status', render: (s) => <StatusBadge status={s.review_status} label={SUBMISSION_STATUS_LABELS[s.review_status]} /> },
                  ]}
                />
              </div>
            ),
          },
          {
            key: 'documents', label: 'Documents', content: (
              <DocumentsSection
                documents={docsData?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('vcollect_project', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('vcollect_project', projectId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('vcollect_project', documentId)}
                queryKeyToInvalidate={['vcollect-project-documents', projectId]}
                hrCategories={false}
              />
            ),
          },
        ]}
      />

      {showGrantModal && (
        <Modal title="Add User to Project" onClose={() => setShowGrantModal(false)}>
          <form onSubmit={handleSubmit((d) => grantMutation.mutate({ ...d, formIds: d.formIds?.map(Number) }))}>
            <label>User
              <select {...register('userId', { required: true, valueAsNumber: true })}>
                <option value="">Select…</option>
                {allUsers.map((u) => <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>)}
              </select>
            </label>
            <label>Project Role
              <select {...register('projectRole', { required: true })}>
                <option value="">Select…</option>
                {PROJECT_ROLES.map((r) => <option key={r} value={r}>{PROJECT_ROLE_LABELS[r]}</option>)}
              </select>
            </label>
            <label>Access
              <select {...register('accessScope')}>
                <option value="entire_project">Entire Project</option>
                <option value="specific_forms">Specific Forms</option>
              </select>
            </label>
            {accessScope === 'specific_forms' && (
              <label>Forms
                <select multiple {...register('formIds')} style={{ minHeight: 90 }}>
                  {forms.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
                </select>
              </label>
            )}
            <div className="form-actions">
              <button type="submit" disabled={grantMutation.isPending}>Grant Project Access</button>
            </div>
          </form>
        </Modal>
      )}

      {showAssignModal && (
        <Modal title="Create Assignment" onClose={() => setShowAssignModal(false)}>
          <form onSubmit={assignForm.handleSubmit((d) => createAssignmentMutation.mutate(d))}>
            <label>Assign To
              <select {...assignForm.register('assigneeType')}>
                <option value="enumerator">Enumerator</option>
                <option value="team">Team</option>
              </select>
            </label>
            <label>{assigneeType === 'team' ? 'Team' : 'Enumerator'}
              <select {...assignForm.register('assigneeId', { required: true, valueAsNumber: true })}>
                <option value="">Select…</option>
                {(assigneeType === 'team' ? teams : enumerators).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </label>
            <label>Form (optional - leave blank for whole project)
              <select {...assignForm.register('formId', { valueAsNumber: true })}>
                <option value="">Whole Project</option>
                {forms.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
              </select>
            </label>
            <div className="form-row">
              <label>State (optional)
                <select {...assignForm.register('stateId')}>
                  <option value="">Select…</option>
                  {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
              <label>LGA
                <select {...assignForm.register('locationId', { valueAsNumber: true })} disabled={!selectedStateId}>
                  <option value="">{selectedStateId ? 'Select…' : 'Select a state first'}</option>
                  {lgas.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </label>
            </div>
            <div className="form-row">
              <label>Start Date <input type="date" {...assignForm.register('collectionStartDate')} /></label>
              <label>End Date <input type="date" {...assignForm.register('collectionEndDate')} /></label>
            </div>
            <div className="form-row">
              <label>Target Count <input type="number" placeholder="e.g. 500" {...assignForm.register('targetCount')} /></label>
              <label>Target Unit <input placeholder="e.g. households" {...assignForm.register('targetDescription')} /></label>
            </div>
            <div className="form-actions">
              <button type="submit" disabled={createAssignmentMutation.isPending}>Create Assignment</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
